import { Directive, HostListener } from '@angular/core';
import { NgControl } from '@angular/forms';
import { normalizeEmail } from '@prism/prism-shared';

// Characters that can legally appear in the addresses we accept (mirrors
// validateEmailRegex) — everything else is swallowed at the keystroke.
const EMAIL_CHAR_REGEX = /^[a-zA-Z0-9._%+\-@]$/;

/**
 * Email input guard for any reactive-forms input (needs NgControl):
 *
 *   <input type="text" emailInput formControlName="email">
 *
 * - keystrokes: characters that can't appear in an email (spaces, quotes, '<>',
 *   commas, ...) are blocked outright, so name-addr junk can't be typed in.
 * - paste: clipboard junk ('"Last, First" <user@host.com>', mailto: links) is replaced
 *   with the extracted bare address immediately; a paste with NO findable address is
 *   blocked entirely.
 *
 * Deliberately no visual invalid state — save paths validate the final value.
 */
@Directive({
  selector: '[emailInput]'
})
export class EmailInputDirective {

  constructor(
    private control: NgControl,
  ) { }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Multi-char key names (Backspace, ArrowLeft, Tab, ...) and shortcuts pass through.
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;
    if (!EMAIL_CHAR_REGEX.test(event.key)) event.preventDefault();
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';
    const email = normalizeEmail(text);
    if (email) this.control.control?.setValue(email);
  }

}
